(() => {
  if (customElements.get('pg-collection-carousel')) return;
  customElements.define('pg-collection-carousel', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const {signal} = this.abort;
      const track = this.querySelector('nav');
      if (!track) return;
      const originals = [...track.children];
      const dotsHost = this.querySelector('[data-carousel-dots]');
      let offsets = [];
      const mobile = matchMedia('(max-width: 767px)');
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      let cycle = 0, visible = false, touching = false, drag = null, suppressUntil = 0, scrolling;
      const focused = () => this.contains(document.activeElement);
      const stop = () => { clearTimeout(this.timer); };
      const schedule = () => {
        stop();
        if (!cycle || !visible || touching || focused() || document.hidden || reduced.matches) return;
        this.timer = setTimeout(() => {
          track.scrollBy({left: originals[1].offsetLeft - originals[0].offsetLeft, behavior:'smooth'});
          schedule();
        }, 4000);
      };
      const syncDots = () => {
        if (!dotsHost || !cycle || !dotsHost.children.length) return;
        const rel = ((track.scrollLeft - cycle) % cycle + cycle) % cycle;
        let best = 0, gap = Math.abs(cycle - rel);
        offsets.forEach((left, index) => { const distance = Math.abs(left - rel); if (distance < gap) { gap = distance; best = index; } });
        [...dotsHost.children].forEach((dot, index) => index === best ? dot.setAttribute('aria-current','true') : dot.removeAttribute('aria-current'));
      };
      const normalize = () => {
        if (!cycle || touching || focused()) return;
        if (track.scrollLeft < cycle * .5) track.scrollLeft += cycle;
        else if (track.scrollLeft >= cycle * 1.5) track.scrollLeft -= cycle;
        syncDots();
      };
      const setup = () => {
        stop();
        track.querySelectorAll('[data-carousel-clone]').forEach(node => node.remove());
        cycle = 0;
        if (mobile.matches && !reduced.matches && originals.length > 1) {
          const clone = (node) => {
            const copy = node.cloneNode(true);
            copy.dataset.carouselClone = 'true';
            copy.setAttribute('aria-hidden','true');
            [copy, ...copy.querySelectorAll('*')].forEach(el => {
              el.removeAttribute('id'); el.removeAttribute('data-shopify-editor-block');
              if (el.matches('a,button,input,select,textarea,[tabindex]')) el.tabIndex = -1;
            });
            return copy;
          };
          track.prepend(...originals.map(clone)); track.append(...originals.map(clone));
          cycle = originals[0].offsetLeft - track.firstElementChild.offsetLeft;
          track.scrollLeft = cycle;
        } else track.scrollLeft = 0;
        offsets = cycle ? originals.map(node => node.offsetLeft - originals[0].offsetLeft) : [];
        if (dotsHost) {
          dotsHost.replaceChildren(...offsets.map(() => document.createElement('span')));
          dotsHost.hidden = !cycle;
          syncDots();
        }
        schedule();
      };
      track.tabIndex = 0;
      track.addEventListener('pointerdown', event => {
        touching = true; stop();
        drag = {id:event.pointerId,x:event.clientX,y:event.clientY,left:track.scrollLeft,type:event.pointerType,moved:false};
      }, {signal});
      window.addEventListener('pointermove', event => {
        if (!drag || event.pointerId !== drag.id) return;
        const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
        if (Math.abs(dx) > 8) drag.moved = true;
        if (drag.type === 'touch' || Math.abs(dx) <= Math.abs(dy) || !drag.moved) return;
        track.dataset.dragging = 'true';
        track.setPointerCapture(event.pointerId);
        event.preventDefault(); track.scrollLeft = drag.left - dx;
      }, {signal});
      const release = event => {
        if (!drag || event.pointerId !== drag.id) return;
        if (drag.moved) suppressUntil = performance.now() + 400;
        drag = null; touching = false; delete track.dataset.dragging;
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
        normalize(); schedule();
      };
      window.addEventListener('pointerup', release, {signal});
      window.addEventListener('pointercancel', release, {signal});
      track.addEventListener('click', event => {
        if (event.detail && performance.now() < suppressUntil) { event.preventDefault(); event.stopImmediatePropagation(); }
      }, {signal,capture:true});
      track.addEventListener('dragstart', event => event.preventDefault(), {signal});
      track.addEventListener('scroll', () => { syncDots(); clearTimeout(scrolling); scrolling = setTimeout(normalize, 160); }, {signal,passive:true});
      track.addEventListener('keydown', event => {
        if (event.target !== track || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        event.preventDefault(); stop();
        const target = event.key === 'Home' ? originals[0] : event.key === 'End' ? originals.at(-1) : null;
        track.scrollTo({left:target ? target.offsetLeft - track.firstElementChild.offsetLeft : track.scrollLeft + (event.key === 'ArrowLeft' ? -1 : 1) * track.clientWidth * .8,behavior:'auto'});
      }, {signal});
      this.addEventListener('focusin', stop, {signal});
      this.addEventListener('focusout', () => queueMicrotask(() => { normalize(); schedule(); }), {signal});
      document.addEventListener('visibilitychange', schedule, {signal});
      mobile.addEventListener('change', setup, {signal}); reduced.addEventListener('change', setup, {signal});
      this.observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; schedule(); }, {threshold:.3});
      this.observer.observe(this);
      let width;
      this.resize = new ResizeObserver(entries => { const next = entries[0].contentRect.width; if (width !== next) { width = next; setup(); } });
      this.resize.observe(track);
      this.cleanup = () => { stop(); clearTimeout(scrolling); this.observer.disconnect(); this.resize.disconnect(); track.querySelectorAll('[data-carousel-clone]').forEach(node => node.remove()); if (dotsHost) { dotsHost.replaceChildren(); dotsHost.hidden = true; } };
      setup();
    }
    disconnectedCallback() { this.abort?.abort(); this.cleanup?.(); }
  });
})();
